/**
 * Place一覧コンテンツ（共通コンポーネント）
 *
 * Place一覧の検索・フィルタ・ソート・選択モード・削除などの機能を提供
 * BottomSheetラッパーなしのコンテンツ部分のみ
 * places-list-sheet.tsx と places-map/index.tsx の両方から利用
 */

import { CheckSquare, Loader2, MapPin, Search, Square, Trash2 } from 'lucide-react'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { deletePlace, deletePlacesBulk } from '@/api/services/overland'
import type { Place } from '@/api/types/overland'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { filterPlacesByQuery, SORT_OPTIONS, type SortOption } from '@/lib/place-utils'

import { GooglePlaceSelectDialog } from './activity-card/google-place-select-dialog'
import { MyPlaceEditDialog } from './my-place-edit-dialog'
import { PlaceFilterBar } from './place-filter-bar'
import { applyFilters, type FilterValue } from './place-filter-logic'
import { PlaceVirtualList } from './place-virtual-list'

type PlaceListContentProps = {
  places: Place[]
  isLoading: boolean
  sort: SortOption
  onSortChange: (sort: SortOption) => void
  onPlacesChange: (places: Place[]) => void
  onSelectPlace: (place: Place) => void
}

export type PlaceListContentRef = {
  /** 外部から再取得ダイアログを開く */
  openReGeocode: (place: Place) => void
}

export const PlaceListContent = forwardRef<PlaceListContentRef, PlaceListContentProps>(function PlaceListContent(
  { places, isLoading, sort, onSortChange, onPlacesChange, onSelectPlace },
  ref,
) {
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Place | null>(null)
  const { isSubmitting: isDeleting, run } = useSubmitLock()

  // 選択モード
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false)

  // フィルタ
  const [filters, setFilters] = useState<FilterValue[]>([])

  // マイプレイス編集ダイアログ
  const [editMyPlaceTarget, setEditMyPlaceTarget] = useState<Place | null>(null)

  // Google再取得ダイアログ
  const [reGeocodeTarget, setReGeocodeTarget] = useState<Place | null>(null)

  useImperativeHandle(
    ref,
    () => ({
      openReGeocode: (place: Place) => setReGeocodeTarget(place),
    }),
    [],
  )

  // 検索フィルタ + Notionフィルタ
  const filteredPlaces = useMemo(() => {
    const result = applyFilters(places, filters)
    return filterPlacesByQuery(result, searchQuery)
  }, [places, filters, searchQuery])

  // Place削除
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return

    await run(async () => {
      try {
        await deletePlace(deleteTarget.id)
        toast.success(`「${deleteTarget.display_name}」を削除しました`)
        setDeleteTarget(null)
        onPlacesChange(places.filter((p) => p.id !== deleteTarget.id))
      } catch {
        toast.error('削除に失敗しました')
      }
    })
  }, [deleteTarget, places, onPlacesChange, run])

  // 選択切り替え
  const toggleSelect = useCallback((placeId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(placeId)) {
        next.delete(placeId)
      } else {
        next.add(placeId)
      }
      return next
    })
  }, [])

  // 全選択/全解除（フィルタ後のみ）
  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredPlaces.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredPlaces.map((p) => p.id)))
    }
  }, [filteredPlaces, selectedIds.size])

  // 選択モード終了
  const handleCancelSelectMode = useCallback(() => {
    setIsSelectMode(false)
    setSelectedIds(new Set())
  }, [])

  // 一括削除
  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return

    await run(async () => {
      try {
        const result = await deletePlacesBulk(Array.from(selectedIds))
        toast.success(result.message)
        onPlacesChange(places.filter((p) => !selectedIds.has(p.id)))
        setSelectedIds(new Set())
        setIsSelectMode(false)
        setIsBulkDeleteDialogOpen(false)
      } catch {
        toast.error('削除に失敗しました')
      }
    })
  }, [selectedIds, places, onPlacesChange, run])

  // アイテムクリックハンドラ（常にonSelectPlaceを呼び、地図移動+詳細表示をトリガー）
  const handlePlaceClick = useCallback(
    (place: Place) => {
      onSelectPlace(place)
    },
    [onSelectPlace],
  )

  const handleReGeocodeUpdated = useCallback(
    (updatedPlace: Place) => {
      if (reGeocodeTarget && reGeocodeTarget.id !== updatedPlace.id) {
        // マージが発生：元のPlaceを削除し、ターゲットPlaceを更新
        onPlacesChange(
          places.filter((p) => p.id !== reGeocodeTarget.id).map((p) => (p.id === updatedPlace.id ? updatedPlace : p)),
        )
        toast.success(`「${reGeocodeTarget.display_name}」を「${updatedPlace.display_name}」にマージしました`)
      } else {
        onPlacesChange(places.map((p) => (p.id === updatedPlace.id ? updatedPlace : p)))
      }
    },
    [reGeocodeTarget, places, onPlacesChange],
  )

  return (
    <>
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="場所を検索..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>
      <div className="flex flex-col h-full px-4 pb-4">
        {/* フィルタバー */}
        <PlaceFilterBar places={places} filters={filters} onFiltersChange={setFilters} />

        {/* ソート・アクションバー */}
        <div className="flex gap-2 shrink-0">
          <Select value={sort} onValueChange={(v) => onSortChange(v as SortOption)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* 選択モード切り替え */}
          {!isSelectMode ? (
            <Button variant="outline" size="icon" onClick={() => setIsSelectMode(true)}>
              <CheckSquare className="size-4" />
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleSelectAll}>
              {selectedIds.size === filteredPlaces.length && filteredPlaces.length > 0 ? (
                <>
                  <Square className="size-4 mr-1" />
                  解除
                </>
              ) : (
                <>
                  <CheckSquare className="size-4 mr-1" />
                  全選択
                </>
              )}
            </Button>
          )}
        </div>

        {/* 件数表示 */}
        <p className="text-xs text-muted-foreground mt-2">
          {filteredPlaces.length}/{places.length}件
        </p>

        {/* Place一覧（BottomSheet のスクロールに乗る仮想リスト） */}
        <div className="mt-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPlaces.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="size-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{searchQuery ? '検索結果がありません' : '登録された場所がありません'}</p>
            </div>
          ) : (
            <PlaceVirtualList
              places={filteredPlaces}
              isSelectMode={isSelectMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onClick={handlePlaceClick}
              onReGeocode={setReGeocodeTarget}
              onEditMyPlace={setEditMyPlaceTarget}
              onDelete={setDeleteTarget}
            />
          )}
        </div>

        {/* 選択モード時のフッター（スクロール領域下端に固定） */}
        {isSelectMode && (
          <div className="sticky bottom-0 z-10 -mx-4 flex items-center justify-between px-4 py-3 border-t bg-background shrink-0">
            <span className="text-sm text-muted-foreground">{selectedIds.size}件選択中</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancelSelectMode} disabled={isDeleting}>
                キャンセル
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsBulkDeleteDialogOpen(true)}
                disabled={selectedIds.size === 0 || isDeleting}
              >
                <Trash2 className="size-4 mr-1" />
                一括削除
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 単体削除確認ダイアログ */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>場所を削除</AlertDialogTitle>
            <AlertDialogDescription>
              「{deleteTarget?.display_name}」を削除しますか？
              <br />
              この場所に紐付いているVisitからも場所情報が解除されます。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  削除中...
                </>
              ) : (
                '削除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 一括削除確認ダイアログ */}
      <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>場所を一括削除</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedIds.size}件の場所を削除しますか？
              <br />
              これらの場所に紐付いているVisitからも場所情報が解除されます。
              <br />
              この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  削除中...
                </>
              ) : (
                '削除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* マイプレイス編集ダイアログ */}
      <MyPlaceEditDialog
        place={editMyPlaceTarget}
        open={!!editMyPlaceTarget}
        onOpenChange={(open) => !open && setEditMyPlaceTarget(null)}
        onUpdated={(updated) => {
          onPlacesChange(
            places.map((p) =>
              p.id === updated.id
                ? {
                    ...p,
                    display_name: updated.name,
                    custom_name: updated.name,
                    radius: updated.radius,
                    wifi_patterns: updated.wifi_patterns,
                  }
                : p,
            ),
          )
        }}
      />

      {/* Google再取得ダイアログ */}
      {reGeocodeTarget && (
        <GooglePlaceSelectDialog
          place={reGeocodeTarget}
          open={!!reGeocodeTarget}
          onOpenChange={(open) => !open && setReGeocodeTarget(null)}
          onUpdated={handleReGeocodeUpdated}
        />
      )}
    </>
  )
})
